/**
 * useMemberMutations — the five things an admin can do to a gym member.
 * Extracted from useMainApp for maintainability.
 *
 * All five are thin clients of a Cloud Function, and that is not an accident:
 * each one has to write an `auditLog` entry alongside its real change, and the
 * `auditLog` create rule is `if false`. A client-side version would either skip
 * the audit entry or be denied — so the atomicity lives on the server, and this
 * file only collects the arguments.
 *
 * The server also owns every rule worth enforcing: privilege escalation,
 * self-edit, last-admin protection, and the ascent/rating cascades behind a
 * kick or a ban. Nothing here re-implements them; the UI still gates on
 * capabilities, but that is presentation, not enforcement.
 *
 * Errors split two ways on purpose. `handleRole` and `handleKick` are fired
 * from a form the admin is still looking at, so they surface a toast AND
 * rethrow (the caller keeps the dialog open). Ban / unban / report return the
 * CF's payload and let the caller decide — their dialogs report their own
 * result.
 */
import { useTranslation } from "../i18n";
import { logEvent, EVT } from "../analytics";
import { haptic } from "../utils/haptics";
import { useToast } from "../components/ui";
import { callableErrorText } from "../utils/callableError";
import { callFunction } from "../utils/callFunction";

export function useMemberMutations(gymId) {
  const { t } = useTranslation();
  const toast = useToast();

  // Role / capability change — goes through the `updateMemberCaps` Cloud
  // Function which atomically writes the member doc update AND the auditLog
  // entry in a single batch (otherwise the audit entry would be blocked by
  // the `auditLog: create: if false` rule). The CF also enforces privilege
  // escalation protection, self-edit protection, and last-admin protection
  // server-side, so the client only has to collect the new values.
  const handleRole = async (uid, role, extraCaps, revokedCaps, action = "role_change") => {
    try {
      await callFunction("updateMemberCaps", {
        gymId,
        targetUid: uid,
        role,
        extraCaps: extraCaps || [],
        revokedCaps: revokedCaps || [],
      });
      // action: "role_change" (quick role select) | "caps_change" (capability
      // editor save with unchanged role) — the caller knows which one it was.
      logEvent(EVT.ADMIN_MEMBER_ACTION, { gym_id: gymId, action });
    } catch (e) {
      console.error("updateMemberCaps failed:", e);
      toast.push({ text: ((t && t('common.error')) || "Error") + ": " + callableErrorText(e, e), durationMs: 6000 });
      throw e;
    }
  };

  // Kick is now server-side — the CF atomically deletes ascents/ratings,
  // updates counters and writes the audit log entry. Keeping it client-side
  // meant the audit entry could silently be skipped if rules blocked it.
  const handleKick = async (uid, reason) => {
    try {
      await callFunction("kickGymMember", { gymId, targetUid: uid, reason });
      haptic("removed");
      logEvent(EVT.ADMIN_MEMBER_ACTION, { gym_id: gymId, action: "kick" });
      // The kick CF deletes the member's ascents server-side; updateGymStats
      // then decrements each affected route.ascentCount, which arrives via the
      // routes listener. No client-side full re-scan needed.
    } catch (e) {
      console.error("kickGymMember failed:", e);
      toast.push({ text: ((t && t('common.error')) || "Error") + ": " + callableErrorText(e, e), durationMs: 6000 });
      throw e;
    }
  };

  // Ban / unban — thin wrappers that call the Cloud Functions. The server
  // handles last-admin protection, cascade deletes, and audit logging.
  const handleBan = async (uid, reason) => {
    const res = await callFunction("banGymMember", { gymId, targetUid: uid, reason });
    // Ban CF cascades ascent deletes server-side; route.ascentCount updates
    // flow back via updateGymStats + the routes listener.
    logEvent(EVT.ADMIN_MEMBER_ACTION, { gym_id: gymId, action: "ban" });
    return res.data;
  };

  const handleUnban = async (uid, reason) => {
    const res = await callFunction("unbanGymMember", { gymId, targetUid: uid, reason: reason || null });
    logEvent(EVT.ADMIN_MEMBER_ACTION, { gym_id: gymId, action: "unban" });
    return res.data;
  };

  // Report a member's GLOBAL display name for PLATFORM moderation review. The
  // gym can only flag; a platform admin decides + resets (the global name is
  // not gym-owned). Server dedups to one pending review per user.
  const handleReportName = async (uid) => {
    const res = await callFunction("reportNameForReview", { gymId, targetUid: uid });
    logEvent(EVT.NAME_REPORTED, { gym_id: gymId });
    return res.data;
  };

  return { handleRole, handleKick, handleBan, handleUnban, handleReportName };
}
